// src/viqai/VIQAIWorkspace.jsx
// ─── Espace conversationnel VIQ.AI ──────────────────────────────────────────
// Passe UX. Changements de fond par rapport à la première version :
//   • UN SEUL hamburger, en haut à gauche, qui ouvre un tiroir glissant depuis
//     la gauche avec voile sombre. Les deux boutons flottants ont disparu.
//   • Composer FIXÉ en bas : il ne descend plus avec la conversation. La zone
//     de messages a son propre scroll (flex:1 + overflow-y), la page non.
//   • Auto-scroll seulement si l'utilisateur était déjà en bas — relire une
//     réponse plus haut ne doit pas être interrompu.
//   • Le texte passe par un vrai renderer Markdown.
//   • États d'outils visibles : « ✦ Identification du véhicule… » → « ✓ Véhicule identifié ».
//   • Le prénom s'affiche même si le backend ne répond pas (repli local).

import { useState, useEffect, useRef, useCallback } from "react";
import ViqCard, { NextActions } from "./ViqCards";
import ViqMarkdown from "./ViqMarkdown";
import { apiGet, apiPatch, apiDelete, streamMessage, compressImageFile } from "./api";
import "./viqai-dark.css";

const MAX_ATTACHMENTS = 8;
const NEAR_BOTTOM_PX = 120;

// Libellé au passé d'un outil terminé : « Véhicule identifié » se lit mieux
// que « identify_vehicle terminé ».
const DONE_LABELS = {
  identify_vehicle: "Véhicule identifié",
  search_dossier: "Dossiers trouvés",
  get_dossier: "Dossier lu",
  create_dossier: "Dossier créé",
  update_dossier: "Dossier mis à jour",
  analyze_photos: "Photos analysées",
  analyze_known_issues: "Fiabilité analysée",
  analyze_histovec: "Rapport HistoVec lu",
  prepare_carvertical: "Accès CarVertical prêt",
  calculate_margin: "Marge calculée",
  analyze_stock: "Portefeuille analysé",
  search_leads: "Leads consultés",
  search_leboncoin: "Recherche marché prête",
  open_lacentrale: "Lien La Centrale prêt",
  analyze_market: "Marché analysé",
  analyze_invoice: "Facture lue",
  save_expense: "Charge enregistrée",
  generate_listing: "Annonce rédigée",
  export_dossier: "Export prêt",
  calculate_malus: "Malus estimé",
  create_note: "Note ajoutée",
  search_notes: "Bloc-notes consulté",
  create_agenda_item: "Ajouté à l'agenda",
  get_agenda: "Agenda consulté",
};

// ── Menu d'import ───────────────────────────────────────────────────────────
// Un seul bouton « + » plutôt que six boutons sur l'interface. Le type choisi
// oriente la formulation envoyée à l'agent : « analyse cette facture » et
// « analyse ces photos » ne déclenchent pas les mêmes outils.
const IMPORT_KINDS = [
  { id: "photos",   icon: "📷", label: "Photos du véhicule", accept: "image/*", hint: "Analyse ces photos du véhicule." },
  { id: "document", icon: "📄", label: "Document",           accept: "image/*,application/pdf", hint: "" },
  { id: "invoice",  icon: "🧾", label: "Facture",            accept: "image/*,application/pdf", hint: "Analyse cette facture." },
  { id: "histovec", icon: "🏛", label: "Rapport HistoVec",   accept: "image/*,application/pdf", hint: "Analyse ce rapport HistoVec." },
  { id: "cg",       icon: "🚘", label: "Carte grise",        accept: "image/*,application/pdf", hint: "Lis cette carte grise." },
  { id: "listing",  icon: "🖼", label: "Capture d'annonce",  accept: "image/*", hint: "Analyse cette annonce." },
];

function ImportMenu({ onPick, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <>
      <span className="viq-menu-catcher" onClick={onClose} />
      <div className="viq-import-menu" role="menu">
        <div className="viq-import-title">Ajouter</div>
        {IMPORT_KINDS.map(k => (
          <button key={k.id} type="button" role="menuitem" className="viq-import-item" onClick={() => onPick(k)}>
            <span aria-hidden="true">{k.icon}</span>{k.label}
          </button>
        ))}
      </div>
    </>
  );
}

// ── Sélecteur de moteur (§33) ───────────────────────────────────────────────
// Un <select> natif ne sait afficher ni description ni badge « Bientôt ». Ce
// menu reste compact — pas de grosse modale — mais donne à chaque moteur sa
// spécialisation, ce qui est le seul moyen pour un utilisateur de choisir
// autre chose qu'au hasard.
//
// §34 — L'étiquette reste « VIQ.AI · Claude », jamais « Claude ». VehicleIQ est
// la couche produit ; le moteur est un détail d'exécution.
function ModelSelector({ providers, value, onChange }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const current = providers.find(p => p.id === value) || providers[0];
  const label = !current || current.id === "auto" ? "VIQ.AI Auto" : `VIQ.AI · ${current.label}`;

  return (
    <div className="viq-model-wrap">
      <button type="button" className="viq-model-btn" aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen(o => !o)}>
        {label} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <>
          <span className="viq-menu-catcher" onClick={() => setOpen(false)} />
          {/* §45-49 — Sur mobile, le dropdown desktop débordait de l'écran et
              tronquait les deux derniers moteurs. En dessous de 700 px, la même
              liste devient une feuille ancrée en bas, avec son propre
              défilement : aucun moteur n'est plus inatteignable. */}
          <div className="viq-model-menu" role="listbox">
            <div className="viq-model-menu-head">
              <span className="viq-import-title">Choisir le moteur</span>
              <button type="button" className="viq-model-close" onClick={() => setOpen(false)} aria-label="Fermer">✕</button>
            </div>
            {providers.map(p => {
              const locked = p.disabled || (!p.available && p.id !== "auto");
              return (
                <button key={p.id} type="button" role="option" aria-selected={p.id === value}
                  className={`viq-model-item${p.id === value ? " on" : ""}${locked ? " locked" : ""}`}
                  disabled={locked}
                  onClick={() => { if (locked) return; onChange(p.id); setOpen(false); }}>
                  <span className="viq-model-line">
                    <b>{p.id === "auto" ? "VIQ.AI Auto" : `VIQ.AI · ${p.label}`}</b>
                    {p.recommended && <span className="viq-badge">Recommandé</span>}
                    {p.coming_soon && <span className="viq-badge soon">Bientôt</span>}
                  </span>
                  {p.summary && <span className="viq-model-desc">{p.summary}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Composer ────────────────────────────────────────────────────────────────
function Composer({
  value, onChange, onSend, busy, attachments, onAttach, onRemoveAttachment,
  providers, provider, onProvider, autoFocus,
}) {
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const pendingKind = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [value]);

  useEffect(() => { if (autoFocus) taRef.current?.focus(); }, [autoFocus]);

  const keyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  };

  const pickKind = (kind) => {
    setMenuOpen(false);
    pendingKind.current = kind;
    if (fileRef.current) { fileRef.current.accept = kind.accept; fileRef.current.click(); }
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const kind = pendingKind.current;
    pendingKind.current = null;
    onAttach(files);
    // Le type d'import pré-remplit la demande : il reste modifiable.
    if (kind?.hint && !value.trim()) onChange(kind.hint);
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("La dictée n'est pas disponible dans ce navigateur."); return; }
    if (recording) { recognitionRef.current?.stop(); setRecording(false); return; }
    const rec = new SR();
    rec.lang = "fr-FR"; rec.continuous = true; rec.interimResults = true;
    const base = value;
    rec.onresult = (ev) => {
      let txt = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) txt += ev.results[i][0].transcript;
      onChange(`${base}${base && !base.endsWith(" ") ? " " : ""}${txt}`);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => setRecording(false);
    recognitionRef.current = rec;
    rec.start();
    setRecording(true);
  };

  return (
    <div className={`viq-composer${recording ? " listening" : ""}`}>
      {attachments.length > 0 && (
        <div className="viq-attachments">
          {attachments.map((a, i) => (
            <span key={i} className="viq-att">
              {a.kind === "image"
                ? <img src={a.data} alt="" />
                : <span className="viq-att-doc" aria-hidden="true">PDF</span>}
              <span className="viq-att-name">{a.name || (a.kind === "pdf" ? "document.pdf" : `photo ${i + 1}`)}</span>
              <button type="button" className="viq-att-x" aria-label="Retirer la pièce jointe"
                onClick={() => onRemoveAttachment(i)}>✕</button>
            </span>
          ))}
        </div>
      )}

      <textarea
        ref={taRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={keyDown}
        rows={1}
        placeholder="Demandez quelque chose à VIQ.AI…"
        aria-label="Demandez quelque chose à VIQ.AI"
      />

      <div className="viq-composer-bar">
        <div className="viq-composer-tools">
          <button type="button" className="viq-tool-btn" aria-haspopup="menu" aria-expanded={menuOpen}
            title="Ajouter une photo ou un document" onClick={() => setMenuOpen(o => !o)}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          {menuOpen && <ImportMenu onPick={pickKind} onClose={() => setMenuOpen(false)} />}
          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" hidden onChange={onFiles} />

          <button type="button" className={`viq-tool-btn${recording ? " recording" : ""}`}
            title={recording ? "Arrêter la dictée" : "Parler à VIQ.AI"} aria-pressed={recording}
            onClick={toggleVoice}>
            {recording ? (
              <span className="viq-wave" aria-hidden="true"><i /><i /><i /><i /></span>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="9" y="3" width="6" height="11" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0" /><line x1="12" y1="18" x2="12" y2="21" />
              </svg>
            )}
          </button>
          {recording && <span className="viq-listening">Écoute…</span>}
        </div>

        <ModelSelector providers={providers} value={provider} onChange={onProvider} />

        <button type="button" className="viq-send" onClick={onSend}
          disabled={busy || (!value.trim() && !attachments.length)} title="Envoyer">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Tiroir de navigation — unique point d'entrée (§3) ──────────────────────
// Toutes les entrées passent par `onNavigate`, branché sur le helper
// centralisé d'App.jsx. Aucune ne construit sa propre logique d'ouverture :
// c'est précisément ce qui laissait des boutons sans effet.
function NavDrawer({
  open, onClose, conversations, activeId, onNew, onOpen, onRename, onDelete,
  onNavigate, userName, isAdmin, darkMode, onToggleTheme, onLogout,
}) {
  const [showAllConv, setShowAllConv] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // §42 — On ouvre la destination AVANT de fermer le tiroir. L'ordre inverse
  // démonte le composant qui porte l'appel et l'action se perd en silence.
  const go = (where) => { onNavigate && onNavigate(where); onClose(); };

  const visible = showAllConv ? conversations : conversations.slice(0, 8);
  // Object.entries suit l'ordre d'insertion : si la première conversation
  // rencontrée datait d'hier, « Hier » s'affichait AVANT « Aujourd'hui ».
  // L'ordre des groupes est donc imposé, pas subi.
  const GROUP_ORDER = ["Aujourd'hui", "Hier", "7 derniers jours", "Plus ancien"];
  const groups = GROUP_ORDER
    .map(g => [g, visible.filter(c => c.group === g)])
    .filter(([, list]) => list.length);

  return (
    <>
      <div className={`viq-scrim${open ? " open" : ""}`} onClick={onClose} aria-hidden="true" />
      <nav className={`viq-drawer${open ? " open" : ""}`} aria-label="Navigation VehicleIQ" aria-hidden={!open}>
        <div className="viq-drawer-brand">VehicleIQ</div>

        <button className="viq-nav-link accent" onClick={() => { onNew(); onClose(); }}>
          <span aria-hidden="true">✦</span> Nouvelle conversation
        </button>
        <div className="viq-drawer-scroll">
        <button className="viq-nav-link" onClick={() => go("dossiers")}>Recherche</button>

        <div className="viq-nav-sep" />
        <button className="viq-nav-link" onClick={() => go("dossiers")}>Dossiers</button>
        <button className="viq-nav-link" onClick={() => go("leads")}>Leads</button>
        <button className="viq-nav-link" onClick={() => go("agenda")}>Agenda</button>
        <button className="viq-nav-link" onClick={() => go("team")}>Équipe</button>

        <div className="viq-nav-sep" />
        <div className="viq-nav-scroll">
          <div className="viq-nav-group">Conversations</div>
          {!conversations.length && <div className="viq-nav-empty">Vos conversations apparaîtront ici.</div>}
          {groups.map(([group, list]) => (
            <div key={group}>
              <div className="viq-nav-group">{group}</div>
              {list.map(c => (
                <div key={c.id} className={`viq-nav-conv${c.id === activeId ? " current" : ""}`}>
                  <button className="viq-nav-conv-open" onClick={() => onOpen(c.id)} title={c.title}>
                    {c.title || "Nouvelle conversation"}
                  </button>
                  <button className="viq-nav-conv-more" title="Renommer"
                    onClick={() => { const t = prompt("Renommer la conversation", c.title); if (t) onRename(c.id, t); }}>⋯</button>
                  <button className="viq-nav-conv-more" title="Supprimer"
                    onClick={() => { if (window.confirm("Supprimer cette conversation ?")) onDelete(c.id); }}>✕</button>
                </div>
              ))}
            </div>
          ))}
          {conversations.length > 8 && !showAllConv && (
            <button className="viq-nav-link muted" onClick={() => setShowAllConv(true)}>
              Voir toutes les conversations
            </button>
          )}
        </div>

        <div className="viq-nav-sep" />
        <button className="viq-nav-link" onClick={() => go("dashboard")}>Dashboard Pro</button>
        <button className="viq-nav-link" onClick={() => go("account")}>{userName ? `Mon espace — ${userName}` : "Mon espace"}</button>
        <button className="viq-nav-link" onClick={() => go("billing")}>Paiements &amp; abonnement</button>
        <button className="viq-nav-link" onClick={() => go("affiliate")}>Programme partenaires</button>
        {/* §9 — Jamais affiché à un non-administrateur. Le helper refuse aussi
            la destination côté App.jsx : deux verrous plutôt qu'un. */}
        {isAdmin && <button className="viq-nav-link" onClick={() => go("admin")}>Administration</button>}

          {/* §29-31 — Remplace le bandeau « Vos avis sur nos services », qui
              nommait encore trois produits séparés. Ici, trois capacités. */}
          <div className="viq-nav-sep" />
          <div className="viq-why">
            <div className="viq-why-title">Pourquoi VIQ.AI</div>
            <div className="viq-why-item"><b>Comprendre</b><span>Véhicule, photos et historique</span></div>
            <div className="viq-why-item"><b>Décider</b><span>Marché, risques et opportunités</span></div>
            <div className="viq-why-item"><b>Agir</b><span>Dossiers, documents et suivi</span></div>
          </div>
        </div>

        {/* Pied du tiroir, hors du scroll : apparence, aide et déconnexion
            restent atteignables même quand la liste des conversations est
            longue — c'est précisément ce qui sortait de l'écran sur mobile. */}
        <div className="viq-drawer-foot">
          <button className="viq-nav-link" onClick={onToggleTheme}>
            <span aria-hidden="true">{darkMode ? "☀" : "☾"}</span>
            {darkMode ? "Thème clair" : "Thème sombre"}
          </button>
          <button className="viq-nav-link muted" onClick={() => go("help")}>Aide &amp; ressources</button>
          <button className="viq-nav-link" onClick={onLogout}>Se déconnecter</button>
        </div>
      </nav>
    </>
  );
}

// ── Footer légal (§33) ──────────────────────────────────────────────────────
// Une ligne, 11 px, `flex: none` : il ne pousse jamais le composer hors écran.
function LegalFooter({ onLegal }) {
  // §54 — Les documents juridiques existent déjà : on appelle leurs handlers,
  // on ne recrée rien. Libellés complets en desktop, abrégés en dessous de
  // 600 px — le title reste complet pour l'accessibilité.
  const links = [
    ["Mentions légales", "Mentions", "mentions"],
    ["Politique de confidentialité", "Confidentialité", "privacy"],
    ["CGU / Confidentialité", "CGU", "cgu"],
    ["Conditions Générales de Vente", "CGV", "cgv"],
  ];
  return (
    <div className="viq-legal">
      {links.map(([full, short, key], i) => (
        <span key={key}>
          <button type="button" title={full} onClick={() => onLegal && onLegal(key)}>
            <span className="legal-full">{full}</span>
            <span className="legal-short">{short}</span>
          </button>
          {i < links.length - 1 && <span aria-hidden="true">·</span>}
        </span>
      ))}
    </div>
  );
}

// ── Perception du travail de l'agent ────────────────────────────────────────
// §20-24, §68-69 — L'indicateur ne disparaît pas dès la première carte : il
// reste tant que `busy`. Une carte rendue au bout de trois secondes alors que
// quatre outils tournent encore laissait croire que la réponse était complète,
// et l'utilisateur repartait avec une conclusion partielle.
function VIQThinking({ steps, busy }) {
  if (!busy && !steps.length) return null;
  if (!steps.length) {
    return (
      <div className="viq-thinking">
        <span className="viq-spark">✦</span> VIQ.AI analyse
        <span className="viq-dots"><i /><i /><i /></span>
      </div>
    );
  }
  return (
    <div className="viq-steps">
      {steps.map((s, i) => (
        <div key={i} className={`viq-step ${s.state}`}>
          <span className="viq-step-mark" aria-hidden="true">
            {s.state === "running" ? "✦" : s.state === "failed" ? "!" : "✓"}
          </span>
          <span>{s.state === "done" ? (DONE_LABELS[s.tool] || s.label) : s.label}</span>
          {s.state === "running" && <span className="viq-dots"><i /><i /><i /></span>}
        </div>
      ))}
      {busy && !steps.some(s => s.state === "running") && (
        <div className="viq-step running">
          <span className="viq-step-mark" aria-hidden="true">✦</span>
          <span>VIQ.AI travaille</span>
          <span className="viq-dots"><i /><i /><i /></span>
        </div>
      )}
    </div>
  );
}

// ── Espace principal ────────────────────────────────────────────────────────
export default function VIQAIWorkspace({
  user, weather, city, dossierId = null,
  onOpenDossier, onOpenInterface, onNavigate, onToggleNotes,
  isAdmin = false, darkMode = true, onToggleTheme, onLogout, onLegal,
  showNotesToggle = false,
}) {
  const [boot, setBoot] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState([]);
  const [provider, setProvider] = useState("auto");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [resolvedConfirms, setResolvedConfirms] = useState({});
  const [serverNext, setServerNext] = useState([]);
  const threadRef = useRef(null);
  const stickToBottom = useRef(true);
  const abortRef = useRef(null);
  // §8 — Compteur de profondeur. `dragleave` se déclenche à chaque passage
  // au-dessus d'un enfant : un simple booléen fait clignoter la zone de dépôt
  // et la fait disparaître au moment du lâcher. Le compteur ne retombe à zéro
  // qu'en sortant réellement du conteneur.
  const dragDepth = useRef(0);

  // Prénom : le backend le fournit, mais l'affichage ne doit pas en dépendre.
  // Sans ce repli, une API indisponible produit « Bonsoir » tout court — et un
  // salut amputé donne l'impression que le produit ne connaît pas son
  // utilisateur, alors que c'est le réseau qui a échoué.
  const localFirstName = (() => {
    const raw = user?.displayName || user?.name || (user?.email ? user.email.split("@")[0] : "");
    const first = String(raw || "").trim().split(/[\s._-]+/)[0];
    if (!first) return null;
    return first.charAt(0).toUpperCase() + first.slice(1);
  })();
  const firstName = boot?.user_name || localFirstName;

  useEffect(() => {
    if (!user?.uid) return;
    apiGet("/api/viqai/bootstrap").then(j => { if (j?.success) setBoot(j); }).catch(() => {});
    apiGet("/api/viqai/conversations").then(j => { if (j?.success) setConversations(j.conversations); }).catch(() => {});
  }, [user?.uid]);

  // Auto-scroll conditionnel : on ne ramène en bas que si l'utilisateur y
  // était déjà. Sinon, relire une réponse précédente devient impossible.
  const onThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };
  useEffect(() => {
    const el = threadRef.current;
    if (el && stickToBottom.current) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [turns, steps]);

  // §6 — Handler UNIQUE : bouton +, glisser-déposer, collage et sélection de
  // fichier finissent tous ici. Avoir plusieurs chemins produisait des états
  // différents selon la manière d'importer — c'est ce qui rendait le
  // glisser-déposer instable.
  const handleIncomingFiles = useCallback(async (files) => {
    const next = [];
    for (const f of files.slice(0, MAX_ATTACHMENTS - attachments.length)) {
      if (f.type === "application/pdf") {
        const data = await new Promise(res => {
          const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(f);
        });
        if (data) next.push({ kind: "pdf", data, name: f.name });
      } else if (f.type.startsWith("image/")) {
        const data = await compressImageFile(f);
        if (data) next.push({ kind: "image", data, name: f.name });
      }
    }
    setAttachments(a => [...a, ...next].slice(0, MAX_ATTACHMENTS));
    return next.length;
  }, [attachments.length]);

  const send = useCallback(async (overrideText, confirmations) => {
    const text = overrideText !== undefined ? overrideText : input;
    if (busy) return;
    if (!text.trim() && !attachments.length && !confirmations) return;

    setBusy(true);
    setSteps([]);
    setServerNext([]);
    stickToBottom.current = true;
    if (overrideText === undefined) setInput("");
    const sent = attachments;
    setAttachments([]);

    setTurns(t => [...t,
      { role: "user", content: text, attachments_count: sent.length,
        thumbs: sent.filter(a => a.kind === "image").map(a => a.data) },
      { role: "assistant", content: "", cards: [] },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamMessage({
        message: text, conversation_id: conversationId, dossier_id: dossierId,
        provider, attachments: sent, confirmations: confirmations || {}, city, weather,
      }, (ev) => {
        if (ev.type === "conversation") {
          setConversationId(ev.id);
          // Insertion sans doublon : une conversation déjà listée (reprise
          // après rechargement) revenait une seconde fois en tête.
          setConversations(c => [
            { id: ev.id, title: ev.title || "Nouvelle conversation", group: "Aujourd'hui", updated_at: new Date().toISOString() },
            ...c.filter(x => x.id !== ev.id),
          ]);
        }
        if (ev.type === "tool_start") {
          setSteps(s => [...s.map(x => x.state === "running" ? { ...x, state: "done" } : x),
                         { tool: ev.tool, label: ev.label, state: "running" }]);
        }
        if (ev.type === "tool_error") {
          setSteps(s => s.map(x => x.tool === ev.tool ? { ...x, state: "failed", label: ev.error } : x));
        }
        if (ev.type === "tool_result" || ev.type === "confirmation") {
          setSteps(s => s.map(x => x.tool === ev.tool ? { ...x, state: "done" } : x));
          const card = ev.type === "confirmation"
            ? { type: "action_confirmation", tool: ev.tool, tool_call_id: ev.tool_call_id, data: ev.data }
            : { type: ev.card, tool: ev.tool, data: ev.data };
          setTurns(t => {
            const c = [...t]; const last = c[c.length - 1];
            c[c.length - 1] = { ...last, cards: [...(last.cards || []), card] };
            return c;
          });
        }
        if (ev.type === "next_actions") setServerNext(Array.isArray(ev.actions) ? ev.actions : []);
        if (ev.type === "text" || ev.type === "final") {
          setTurns(t => {
            const c = [...t]; const last = c[c.length - 1];
            c[c.length - 1] = { ...last, content: ev.text || last.content };
            return c;
          });
        }
        if (ev.type === "error") {
          setTurns(t => {
            const c = [...t];
            c[c.length - 1] = { ...c[c.length - 1], content: ev.message || "La demande n'a pas abouti." };
            return c;
          });
        }
      }, controller.signal);
    } catch (e) {
      setTurns(t => {
        const c = [...t];
        c[c.length - 1] = { ...c[c.length - 1], content: e.message || "La demande n'a pas abouti." };
        return c;
      });
    } finally {
      setBusy(false);
      setSteps(s => s.map(x => x.state === "running" ? { ...x, state: "done" } : x));
      // La conversation active remonte en tête et change de groupe : le tri de
      // la sidebar se fait sur `updated_at`, qui vient de changer côté serveur.
      // Sans cette remontée, l'ordre affiché restait celui du chargement de la
      // page et une conversation reprise ce matin passait sous celles d'hier.
      setConversations(c => {
        const id = conversationId;
        if (!id) return c;
        const hit = c.find(x => x.id === id);
        if (!hit) return c;
        return [{ ...hit, group: "Aujourd'hui", updated_at: new Date().toISOString() },
                ...c.filter(x => x.id !== id)];
      });
    }
  }, [input, attachments, busy, conversationId, dossierId, provider, city, weather]);

  // v188 — CORRECTIF : ce gestionnaire était placé AVANT `const send`, avec
  // `[send]` en tableau de dépendances. Ce tableau est évalué à chaque rendu :
  // lire `send` avant sa déclaration levait une ReferenceError de zone morte
  // temporelle, et l'espace user devenait inaccessible (le site public, qui ne
  // monte pas ce composant, continuait de fonctionner).
  //
  // La référence passe par un ref : l'écouteur est posé une seule fois et lit
  // toujours la dernière version de `send`, sans le mettre en dépendance.
  const sendRef = useRef(send);
  useEffect(() => { sendRef.current = send; }, [send]);
  useEffect(() => {
    const ask = (e) => { const q = e.detail?.prompt; if (q) sendRef.current?.(q); };
    window.addEventListener("viq-ask", ask);
    return () => window.removeEventListener("viq-ask", ask);
  }, []);

  const confirmAction = (card) => {
    setResolvedConfirms(r => ({ ...r, [card.tool_call_id]: true }));
    send(`Confirmé : ${card.data.label}`, { [card.tool_call_id]: true, [card.tool]: true });
  };
  const cancelAction = (card) => {
    setResolvedConfirms(r => ({ ...r, [card.tool_call_id]: "cancelled" }));
    setTurns(t => [...t, { role: "assistant", content: "Action annulée. Rien n'a été enregistré.", cards: [] }]);
  };

  const openConversation = async (id) => {
    setDrawerOpen(false);
    const j = await apiGet(`/api/viqai/conversations/${id}`).catch(() => null);
    if (!j?.success) return;
    setConversationId(id);
    setProvider(j.conversation.provider || "auto");
    setTurns(j.messages.map(m => ({
      role: m.role, content: m.content, cards: m.cards || [], attachments_count: m.attachments_count,
    })));
    stickToBottom.current = true;
  };

  const newConversation = () => {
    setConversationId(null); setTurns([]); setSteps([]);
    setInput(""); setAttachments([]); setDrawerOpen(false);
  };

  const navigate = (where) => { setDrawerOpen(false); onNavigate && onNavigate(where); };

  // §31-34, §71-74 — Exécution d'une action suggérée. Deux natures distinctes :
  //   • navigate / tel / open  → exécutées DIRECTEMENT côté interface. Passer
  //     « ouvrir le dossier 42 » par le modèle coûterait un aller-retour, une
  //     latence et un appel facturé pour une action déjà déterminée.
  //   • prompt / tool          → envoyées à VIQ.AI.
  // Dans tous les cas le composer est vidé : laisser la demande affichée après
  // l'avoir envoyée donne l'impression qu'elle n'est pas partie.
  const runSuggestedAction = (a) => {
    if (!a) return;
    setInput("");
    if (a.type === "navigate" && a.destination) return onNavigate && onNavigate(a.destination);
    if (a.type === "open_dossier" && a.id) return onOpenDossier && onOpenDossier(a.id);
    if (a.type === "network" && a.id) { window.dispatchEvent(new CustomEvent("viq-open-network", { detail: { id: a.id } })); return; }
    if (a.type === "tel" && a.phone) { window.location.href = `tel:${String(a.phone).replace(/[\s.]/g, "")}`; return; }
    if (a.type === "link" && a.url) { window.open(a.url, "_blank", "noopener,noreferrer"); return; }
    const text = a.prompt || a.ask || a.label;
    if (!text) return;
    // Une formulation qui se termine par « : » ou un espace attend un
    // complément de l'utilisateur : on la dépose dans le composer au lieu de
    // l'envoyer incomplète.
    if (/[\s:]$/.test(text)) { setInput(text); return; }
    send(text);
  };

  const empty = turns.length === 0;
  const hour = new Date().getHours();
  const greeting = boot?.greeting
    || (hour < 6 ? "Bonne nuit" : hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir");

  const composer = (
    <Composer value={input} onChange={setInput} onSend={() => send()} busy={busy}
      attachments={attachments} onAttach={handleIncomingFiles}
      onRemoveAttachment={i => setAttachments(a => a.filter((_, k) => k !== i))}
      providers={boot?.providers || [{ id: "auto", label: "Auto", available: true }]}
      provider={provider} onProvider={setProvider} autoFocus={empty} />
  );

  return (
    <div className={`viq-chat${drawerOpen ? " drawer-open" : ""}`}
      onDragEnter={e => {
        e.preventDefault();
        if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={e => e.preventDefault()}
      onDragLeave={e => {
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
      }}
      onDrop={e => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        // Le texte déjà saisi n'est jamais touché : déposer une photo ne doit
        // pas effacer une question en cours de rédaction.
        handleIncomingFiles(Array.from(e.dataTransfer?.files || []));
      }}
      onPaste={e => {
        const files = Array.from(e.clipboardData?.files || []);
        if (files.length) { e.preventDefault(); handleIncomingFiles(files); }
      }}>

      {dragging && (
        <div className="viq-dropzone">
          <strong>Déposez vos photos ici</strong>
          <span>VIQ.AI les analysera automatiquement</span>
        </div>
      )}

      {/* §47 — Après suppression de la navbar, le haut se réduit à l'essentiel :
          le hamburger et l'accès à une nouvelle conversation. Tout le reste de
          la hauteur revient à la conversation. */}
      <header className="viq-topbar">
        <button type="button" className="viq-tool-btn" aria-label="Ouvrir la navigation"
          aria-expanded={drawerOpen}
          onClick={() => {
            setDrawerOpen(true);
            // Resynchronisation à l'ouverture : les titres sont dérivés côté
            // serveur du premier message, et une conversation ouverte dans un
            // autre onglet n'apparaîtrait jamais autrement.
            apiGet("/api/viqai/conversations")
              .then(j => { if (j?.success) setConversations(j.conversations); })
              .catch(() => {});
          }}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <button type="button" className="viq-topbar-new" onClick={newConversation}
          title="Démarrer une nouvelle conversation">
          {conversationId
            ? (conversations.find(c => c.id === conversationId)?.title || "Conversation")
            : "Nouvelle conversation"}
        </button>
        {/* §5 — Le bouton notes n'existe QUE sur petit écran. Sur desktop le
            bloc-notes est permanent : un bouton pour l'ouvrir n'y a aucun sens,
            et une croix pour le fermer non plus. */}
        {showNotesToggle && (
          <button type="button" className="viq-tool-btn viq-notes-toggle" aria-label="Ouvrir le bloc-notes"
            onClick={onToggleNotes}>
            <span aria-hidden="true">📝</span>
          </button>
        )}
      </header>

      <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
        conversations={conversations} activeId={conversationId}
        onNew={newConversation} onOpen={openConversation}
        onRename={async (id, title) => {
          await apiPatch(`/api/viqai/conversations/${id}`, { title }).catch(() => {});
          setConversations(c => c.map(x => x.id === id ? { ...x, title } : x));
        }}
        onDelete={async (id) => {
          await apiDelete(`/api/viqai/conversations/${id}`).catch(() => {});
          setConversations(c => c.filter(x => x.id !== id));
          if (id === conversationId) newConversation();
        }}
        onNavigate={navigate} userName={firstName}
        isAdmin={isAdmin} darkMode={darkMode}
        onToggleTheme={onToggleTheme} onLogout={onLogout} />

      {empty ? (
        <div className="viq-welcome">
          <div className="viq-welcome-inner">
            <h1>{greeting}{firstName ? ` ${firstName}` : ""}</h1>
            <div className="viq-meta">
              {[weather, city].filter(Boolean).join(" · ")}
              {/* §44 (passe précédente) — l'expiration ne doit jamais ressembler
                  à une panne : on annonce ce qui reste, et à son terme on
                  oriente vers les forfaits, sans message d'erreur. */}
              {boot?.trial?.status === "active" && boot.trial.days_left != null && (
                <span className="viq-trial"> · Essai VIQ.AI — {boot.trial.days_left} jour{boot.trial.days_left > 1 ? "s" : ""} restant{boot.trial.days_left > 1 ? "s" : ""}</span>
              )}
              {boot?.trial?.status === "expired" && (
                <button type="button" className="viq-trial ended" onClick={() => navigate("billing")}>
                  · Essai terminé — découvrir les forfaits VIQ.AI
                </button>
              )}
            </div>
            <div className="viq-ask">Que souhaitez-vous faire ?</div>
            {composer}
            <div className="viq-suggestions">
              {(boot?.suggestions || []).map((s, i) => (
                <button key={i} type="button" className="viq-suggestion"
                  onClick={() => runSuggestedAction(s)}>{s.label}</button>
              ))}
            </div>
          </div>
          <LegalFooter onLegal={onLegal} />
        </div>
      ) : (
        <>
          <div className="viq-thread" ref={threadRef} onScroll={onThreadScroll}>
            {turns.map((t, i) => (
              <div key={i} className={`viq-turn ${t.role}`}>
                {t.role === "user" ? (
                  <div className="viq-bubble-user">
                    {t.content}
                    {t.attachments_count > 0 && (
                      <>
                        <div className="viq-att-count">
                          {t.attachments_count} pièce{t.attachments_count > 1 ? "s" : ""} jointe{t.attachments_count > 1 ? "s" : ""}
                        </div>
                        {t.thumbs?.length > 0 && (
                          <div className="viq-gallery">
                            {t.thumbs.slice(0, 3).map((src, k) => <img key={k} src={src} alt="" />)}
                            {t.thumbs.length > 3 && <span className="more">+{t.thumbs.length - 3}</span>}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    {i === turns.length - 1 && steps.length === 0 && <VIQThinking steps={steps} busy={busy} />}
                    {(t.cards || []).map((c, k) => (
                      <ViqCard key={k} card={c}
                        onAsk={(q) => runSuggestedAction({ prompt: q })}
                        onAction={runSuggestedAction}
                        onOpenDossier={onOpenDossier}
                        onOpenInterface={onOpenInterface}
                        onConfirm={confirmAction}
                        onCancel={cancelAction}
                        onUpgrade={() => navigate("billing")}
                        resolved={resolvedConfirms[c.tool_call_id]} />
                    ))}
                    {/* §34 — Les prochaines actions n'apparaissent que sous la
                        DERNIÈRE carte du dernier tour : les répéter sous chaque
                        carte d'un historique transformerait la conversation en
                        mur de boutons. */}
                    {/* §23 — Les suggestions viennent du serveur, qui connaît tout
                        le contexte (CarVertical déjà préparé, HistoVec lu, prix
                        d'achat connu…). La liste locale par carte ne sert plus
                        que de repli si le flux n'en a pas envoyé. */}
                    {i === turns.length - 1 && !busy && (serverNext.length > 0 ? (
                      <div className="viq-next">
                        {serverNext.map((a, k) => (
                          <button key={k} type="button"
                            className={`viq-suggestion${a.primary ? " primary" : ""}`}
                            onClick={() => runSuggestedAction(a)}>
                            {a.icon ? `${a.icon} ` : ""}{a.label}
                          </button>
                        ))}
                      </div>
                    ) : (t.cards || []).length > 0 && (
                      <NextActions card={t.cards[t.cards.length - 1]}
                        onAsk={(q) => runSuggestedAction({ prompt: q })} />
                    ))}
                    {t.content && <ViqMarkdown className="viq-answer">{t.content}</ViqMarkdown>}
                    {/* Les étapes restent SOUS le contenu déjà rendu : le
                        travail en cours se lit à la suite de ce qui est acquis. */}
                    {i === turns.length - 1 && steps.length > 0 && <VIQThinking steps={steps} busy={busy} />}
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="viq-composer-wrap">{composer}</div>
          <LegalFooter onLegal={onLegal} />
        </>
      )}
    </div>
  );
}
